import { useEffect, useState } from 'react'
import { api, getToken } from '../api'
import { Card, Button, Form, Input, InputNumber, Select, Tag, Empty, Spin, Space, Typography, Row, Col, Drawer, Upload, message, Popconfirm, Statistic, DatePicker, List, Modal } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined, ArrowUpOutlined, ArrowDownOutlined, WarningOutlined, CameraOutlined, CalendarOutlined, EuroOutlined, SearchOutlined, TagsOutlined, InfoCircleOutlined, HistoryOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography
const { TextArea } = Input

const COLORES_DISPONIBLES = [
  { value: 'green', label: 'Verde' },
  { value: 'red', label: 'Rojo' },
  { value: 'orange', label: 'Naranja' },
  { value: 'purple', label: 'Morado' },
  { value: 'magenta', label: 'Magenta' },
  { value: 'geekblue', label: 'Azul oscuro' },
  { value: 'cyan', label: 'Cian' },
  { value: 'lime', label: 'Lima' },
  { value: 'blue', label: 'Azul' },
  { value: 'gold', label: 'Dorado' },
  { value: 'volcano', label: 'Volcán' },
  { value: 'default', label: 'Gris' },
]

const UNIDADES = ['litros', 'kg', 'unidades', 'sacos', 'metros', 'gramos', 'ml']

const TIPOS_IVA = [
  { value: 4, label: '4% (superreducido)' },
  { value: 10, label: '10% (reducido)' },
  { value: 21, label: '21% (general)' },
]

export default function Inventario({ usuario }) {
  const isMobile = useIsMobile()
  const soloLectura = usuario?.rol === 'tecnico' || usuario?.rol === 'usuario'
  const [productos, setProductos] = useState([])
  const [categorias, setCategorias] = useState([])
  const [stats, setStats] = useState({ total_productos: 0, stock_bajo: 0, valor_total: 0, gastado: 0 })
  const [filtroPeriodo, setFiltroPeriodo] = useState('mes')
  const [fechasCustom, setFechasCustom] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [mostrarMovimiento, setMostrarMovimiento] = useState(false)
  const [mostrarCategorias, setMostrarCategorias] = useState(false)
  const [catEditando, setCatEditando] = useState(null)
  const [mostrarFicha, setMostrarFicha] = useState(false)
  const [productoFicha, setProductoFicha] = useState(null)
  const [movimientosFicha, setMovimientosFicha] = useState([])
  const [cargandoFicha, setCargandoFicha] = useState(false)
  const [editando, setEditando] = useState(null)
  const [productoMov, setProductoMov] = useState(null)
  const [movEditando, setMovEditando] = useState(null)
  const [filtroCategoria, setFiltroCategoria] = useState(null)
  const [busqueda, setBusqueda] = useState('')
  const [identificando, setIdentificando] = useState(false)
  const [cultivos, setCultivos] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [tipoMov, setTipoMov] = useState('entrada')
  const [form] = Form.useForm()
  const [formMov] = Form.useForm()
  const [formCat] = Form.useForm()

  const colorCategoria = (nombre) => {
    const c = categorias.find(x => x.nombre === nombre)
    return c ? c.color : 'default'
  }

  const getFechasFiltro = (periodo, custom) => {
    const hoy = dayjs()
    if (periodo === 'mes') return { desde: hoy.startOf('month').format('YYYY-MM-DD'), hasta: hoy.endOf('month').format('YYYY-MM-DD') }
    if (periodo === 'anio') return { desde: hoy.startOf('year').format('YYYY-MM-DD'), hasta: hoy.endOf('year').format('YYYY-MM-DD') }
    if (periodo === 'custom' && custom && custom[0] && custom[1]) return { desde: custom[0].format('YYYY-MM-DD'), hasta: custom[1].format('YYYY-MM-DD') }
    if (periodo === 'todo') return {}
    return { desde: hoy.startOf('month').format('YYYY-MM-DD'), hasta: hoy.endOf('month').format('YYYY-MM-DD') }
  }

  useEffect(() => { cargar() }, [filtroPeriodo, fechasCustom])

  const cargar = async () => {
    try {
      const fechas = getFechasFiltro(filtroPeriodo, fechasCustom)
      const params = new URLSearchParams(fechas).toString()
      const [prods, st, cats] = await Promise.all([
        api('/productos'),
        api(`/inventario/stats?${params}`),
        api('/categorias'),
      ])
      setProductos(prods)
      setStats(st)
      setCategorias(cats)
    } catch (err) {
      console.error('Error cargando inventario:', err)
    }
    setCargando(false)
  }

  const guardarCategoria = async (values) => {
    try {
      if (catEditando) {
        await api(`/categorias/${catEditando.id}`, { method: 'PUT', body: JSON.stringify(values) })
        message.success('Categoría actualizada')
      } else {
        await api('/categorias', { method: 'POST', body: JSON.stringify(values) })
        message.success('Categoría creada')
      }
      formCat.resetFields()
      setCatEditando(null)
      const cats = await api('/categorias')
      setCategorias(cats)
      cargar()
    } catch (err) {
      message.error(err?.message || 'Error guardando categoría')
    }
  }

  const eliminarCategoria = async (cat) => {
    try {
      await api(`/categorias/${cat.id}`, { method: 'DELETE' })
      message.success('Categoría eliminada')
      const cats = await api('/categorias')
      setCategorias(cats)
      cargar()
    } catch (err) {
      const msg = err?.message || ''
      if (msg.includes('producto')) {
        Modal.confirm({
          title: 'Categoría en uso',
          content: `${msg} ¿Quieres reasignar esos productos a otra categoría?`,
          okText: 'Reasignar',
          cancelText: 'Cancelar',
          onOk: () => {
            Modal.confirm({
              title: `Reasignar productos de "${cat.nombre}"`,
              content: (
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Selecciona categoría destino"
                  onChange={(v) => { window.__catReasignar = v }}
                >
                  {categorias.filter(c => c.id !== cat.id).map(c => (
                    <Select.Option key={c.id} value={c.nombre}>{c.nombre}</Select.Option>
                  ))}
                </Select>
              ),
              okText: 'Reasignar y eliminar',
              cancelText: 'Cancelar',
              onOk: async () => {
                const destino = window.__catReasignar
                if (!destino) { message.warning('Selecciona una categoría destino'); return Promise.reject() }
                try {
                  await api(`/categorias/${cat.id}?reasignar_a=${encodeURIComponent(destino)}`, { method: 'DELETE' })
                  message.success('Categoría eliminada y productos reasignados')
                  delete window.__catReasignar
                  const cats = await api('/categorias')
                  setCategorias(cats)
                  cargar()
                } catch (e) {
                  message.error(e?.message || 'Error reasignando')
                }
              },
            })
          },
        })
      } else {
        message.error(msg || 'Error eliminando categoría')
      }
    }
  }

  const abrirNuevo = () => {
    setEditando(null)
    form.resetFields()
    setMostrarForm(true)
  }

  const abrirEdicion = (prod) => {
    setEditando(prod)
    form.setFieldsValue({
      nombre: prod.nombre,
      categoria: prod.categoria,
      descripcion: prod.descripcion,
      unidad_medida: prod.unidad_medida,
      stock_actual: prod.stock_actual,
      stock_minimo: prod.stock_minimo,
      precio_unitario: prod.precio_unitario,
      iva: prod.iva ?? 10,
      uso_recomendado: prod.uso_recomendado,
      fecha_compra: prod.fecha_compra ? dayjs(prod.fecha_compra) : null,
    })
    setMostrarForm(true)
  }

  const guardar = async (values) => {
    setGuardando(true)
    const payload = { ...values, fecha_compra: values.fecha_compra ? values.fecha_compra.format('YYYY-MM-DD') : null }
    try {
      if (editando) {
        await api(`/productos/${editando.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        message.success('Producto actualizado')
      } else {
        await api('/productos', { method: 'POST', body: JSON.stringify(payload) })
        message.success('Producto creado')
      }
      setMostrarForm(false)
      form.resetFields()
      setEditando(null)
      cargar()
    } catch (err) {
      message.error('Error guardando producto')
    }
    setGuardando(false)
  }

  const eliminar = async (id) => {
    try {
      await api(`/productos/${id}`, { method: 'DELETE' })
      message.success('Producto eliminado')
      cargar()
    } catch (err) {
      message.error('Error eliminando producto')
    }
  }

  const abrirFicha = async (prod) => {
    setProductoFicha(prod)
    setMostrarFicha(true)
    setCargandoFicha(true)
    setMovimientosFicha([])
    try {
      const movs = await api(`/productos/${prod.id}/movimientos`)
      setMovimientosFicha(movs)
    } catch (err) {
      console.error('Error cargando movimientos:', err)
    }
    setCargandoFicha(false)
  }

  const abrirMovimiento = async (prod) => {
    setProductoMov(prod)
    setMovEditando(null)
    formMov.resetFields()
    formMov.setFieldsValue({ tipo: 'entrada', fecha: dayjs() })
    setTipoMov('entrada')
    setMostrarMovimiento(true)
    try {
      const [c, p] = await Promise.all([api('/cultivos'), api('/parcelas')])
      setCultivos(c)
      setParcelas(p)
    } catch (err) { console.error(err) }
  }

  const abrirEdicionMovimiento = async (prod, mov) => {
    setProductoMov(prod)
    setMovEditando(mov)
    formMov.resetFields()
    formMov.setFieldsValue({
      tipo: mov.tipo,
      fecha: mov.fecha ? dayjs(mov.fecha) : (mov.created_at ? dayjs(mov.created_at) : dayjs()),
      cantidad: Number(mov.cantidad),
      parcela_ids: mov.parcela_id ? [mov.parcela_id] : [],
      motivo: mov.motivo,
    })
    setTipoMov(mov.tipo)
    setMostrarMovimiento(true)
    try {
      const [c, p] = await Promise.all([api('/cultivos'), api('/parcelas')])
      setCultivos(c)
      setParcelas(p)
    } catch (err) { console.error(err) }
  }

  const refrescarFicha = async (prodId) => {
    try {
      const movs = await api(`/productos/${prodId}/movimientos`)
      setMovimientosFicha(movs)
    } catch (err) { console.error('Error recargando movimientos:', err) }
  }

  const guardarMovimiento = async (values) => {
    const payload = { ...values, fecha: values.fecha ? values.fecha.format('YYYY-MM-DD') : null, parcela_ids: values.parcela_ids || [] }
    delete payload.parcela_id
    try {
      if (movEditando) {
        await api(`/productos/${productoMov.id}/movimientos/${movEditando.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
        message.success('Movimiento actualizado')
      } else {
        await api(`/productos/${productoMov.id}/movimientos`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        message.success(`${values.tipo === 'entrada' ? 'Entrada' : 'Salida'} registrada`)
      }
      setMostrarMovimiento(false)
      setMovEditando(null)
      if (mostrarFicha && productoFicha) refrescarFicha(productoFicha.id)
      cargar()
    } catch (err) {
      message.error(movEditando ? 'Error actualizando movimiento' : 'Error registrando movimiento')
    }
  }

  const eliminarMovimiento = async (prod, mov) => {
    try {
      await api(`/productos/${prod.id}/movimientos/${mov.id}`, { method: 'DELETE' })
      message.success('Movimiento eliminado')
      if (mostrarFicha && productoFicha) refrescarFicha(productoFicha.id)
      cargar()
    } catch (err) {
      message.error('Error eliminando movimiento')
    }
  }

  const identificarPorFoto = async (file) => {
    setIdentificando(true)
    try {
      const formData = new FormData()
      formData.append('foto', file)
      const token = getToken()
      const res = await fetch('/api/inventario/identificar', {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      })
      const data = await res.json()
      if (data.error) {
        message.error(data.error)
      } else if (data.nombre) {
        // Resetear estado para crear producto nuevo (no sobreescribir uno existente)
        setEditando(null)
        form.resetFields()
        const nombresCat = categorias.map(c => c.nombre)
        form.setFieldsValue({
          nombre: data.nombre,
          categoria: nombresCat.includes(data.categoria) ? data.categoria : 'otro',
          descripcion: data.descripcion || '',
          unidad_medida: UNIDADES.includes(data.unidad_medida) ? data.unidad_medida : 'unidades',
          uso_recomendado: data.uso_recomendado || '',
        })
        setMostrarForm(true)
        message.success(`Identificado: ${data.nombre}`)
      } else {
        message.warning('No se pudo identificar el producto')
      }
    } catch (err) {
      console.error('Error identificando:', err)
      message.error('Error al procesar la imagen')
    }
    setIdentificando(false)
    return false
  }

  const categoriasUsadas = categorias.length > 0 ? categorias.map(c => c.nombre) : [...new Set(productos.map(p => p.categoria).filter(Boolean))]
  const textoBusqueda = busqueda.trim().toLowerCase()
  const productosFiltrados = productos.filter(p => {
    if (filtroCategoria && p.categoria !== filtroCategoria) return false
    if (textoBusqueda && !(p.nombre || '').toLowerCase().includes(textoBusqueda)) return false
    return true
  })
  const productosConStock = productosFiltrados.filter(p => Number(p.stock_actual) > 0)
  const productosSinStock = productosFiltrados.filter(p => Number(p.stock_actual) <= 0)

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Inventario</Title>
        {!soloLectura && (
          <Space>
            <Button icon={<TagsOutlined />} onClick={() => setMostrarCategorias(true)} ghost style={{ flexShrink: 0 }}>
              {!isMobile && 'Categorías'}
            </Button>
            <Upload accept="image/*" capture="environment" beforeUpload={identificarPorFoto} showUploadList={false}>
              <Button icon={<CameraOutlined />} loading={identificando} ghost style={{ flexShrink: 0 }}>
                {!isMobile && (identificando ? 'Identificando...' : 'Foto')}
              </Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>
              {!isMobile && 'Nuevo'}
            </Button>
          </Space>
        )}
      </div>

      {/* Drawer producto */}
      <Drawer
        title={editando ? 'Editar producto' : 'Nuevo producto'}
        open={mostrarForm}
        onClose={() => { setMostrarForm(false); setEditando(null); form.resetFields() }}
        width={isMobile ? '100%' : 400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setMostrarForm(false); setEditando(null); form.resetFields() }}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              {editando ? 'Actualizar' : 'Crear producto'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input placeholder="Ej: Roundup, Urea 46%..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Categoría" name="categoria" rules={[{ required: true, message: 'Obligatorio' }]}>
                <Select placeholder="Selecciona">
                  {categorias.map(c => <Select.Option key={c.id} value={c.nombre}>{c.nombre}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Unidad" name="unidad_medida" rules={[{ required: true, message: 'Obligatorio' }]}>
                <Select placeholder="Selecciona">
                  {UNIDADES.map(u => <Select.Option key={u} value={u}>{u}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Descripción" name="descripcion">
            <TextArea rows={2} placeholder="Descripción del producto..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Stock actual" name="stock_actual">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Stock mínimo" name="stock_minimo">
                <InputNumber min={0} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Precio base/ud (€)" name="precio_unitario">
                <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="IVA" name="iva" initialValue={10}>
                <Select>
                  {TIPOS_IVA.map(t => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Fecha de compra" name="fecha_compra">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Selecciona fecha" />
          </Form.Item>
          <Form.Item label="Uso recomendado" name="uso_recomendado">
            <TextArea rows={2} placeholder="Para qué se usa..." />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Drawer ficha producto */}
      <Drawer
        title={productoFicha ? <Space><InfoCircleOutlined /> {productoFicha.nombre}</Space> : 'Ficha producto'}
        open={mostrarFicha}
        onClose={() => { setMostrarFicha(false); setProductoFicha(null); setMovimientosFicha([]) }}
        width={isMobile ? '100%' : 460}
        footer={productoFicha && !soloLectura && (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Popconfirm title="¿Eliminar producto?" onConfirm={() => { eliminar(productoFicha.id); setMostrarFicha(false) }} okText="Sí" cancelText="No">
              <Button danger icon={<DeleteOutlined />}>Eliminar</Button>
            </Popconfirm>
            <Space>
              <Button icon={<EditOutlined />} onClick={() => { setMostrarFicha(false); abrirEdicion(productoFicha) }}>Editar</Button>
              <Button type="primary" icon={<InboxOutlined />} onClick={() => { setMostrarFicha(false); abrirMovimiento(productoFicha) }} style={{ background: '#1B3A6B' }}>
                Movimiento
              </Button>
            </Space>
          </Space>
        )}
      >
        {productoFicha && (() => {
          const p = productoFicha
          const stockBajo = p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo
          const sinStock = Number(p.stock_actual) <= 0
          const iva = p.iva ?? 10
          const precioConIva = p.precio_unitario ? p.precio_unitario * (1 + iva / 100) : null
          const valorTotal = precioConIva ? p.stock_actual * precioConIva : null
          return (
            <>
              <Space wrap style={{ marginBottom: 12 }}>
                <Tag color={colorCategoria(p.categoria)}>{p.categoria}</Tag>
                {sinStock && <Tag color="default">Sin stock</Tag>}
                {!sinStock && stockBajo && <Tag color="red"><WarningOutlined /> Stock bajo</Tag>}
              </Space>

              <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                <Row gutter={12}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Stock actual</Text>
                    <div><Text strong style={{ fontSize: 22, color: sinStock ? '#999' : (stockBajo ? '#ff4d4f' : '#2E7D32') }}>
                      {p.stock_actual} {p.unidad_medida}
                    </Text></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Stock mínimo</Text>
                    <div><Text strong style={{ fontSize: 16 }}>{p.stock_minimo || 0} {p.unidad_medida}</Text></div>
                  </Col>
                </Row>
              </Card>

              {p.precio_unitario > 0 && (
                <Card size="small" title={<Space><EuroOutlined /> Precio</Space>} style={{ marginBottom: 12 }}>
                  <div><Text>Base: <Text strong>{p.precio_unitario} €/{p.unidad_medida}</Text></Text></div>
                  <div><Text type="secondary" style={{ fontSize: 12 }}>IVA: {iva}%</Text></div>
                  <div><Text>Con IVA: <Text strong style={{ color: '#E65100' }}>{precioConIva.toFixed(2)} €/{p.unidad_medida}</Text></Text></div>
                  {valorTotal !== null && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Valor stock total: </Text>
                      <Text strong style={{ color: '#E65100' }}>{valorTotal.toFixed(2)} €</Text>
                    </div>
                  )}
                </Card>
              )}

              {p.descripcion && (
                <Card size="small" title="Descripción" style={{ marginBottom: 12 }}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{p.descripcion}</Text>
                </Card>
              )}

              {p.uso_recomendado && (
                <Card size="small" title="Uso recomendado" style={{ marginBottom: 12 }}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{p.uso_recomendado}</Text>
                </Card>
              )}

              {p.fecha_compra && (
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary"><CalendarOutlined /> Fecha de compra: </Text>
                  <Text strong>{dayjs(p.fecha_compra).format('DD/MM/YYYY')}</Text>
                </div>
              )}

              <Card size="small" title={<Space><HistoryOutlined /> Movimientos ({movimientosFicha.length})</Space>}>
                {cargandoFicha ? (
                  <div style={{ textAlign: 'center', padding: 12 }}><Spin /></div>
                ) : movimientosFicha.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>Sin movimientos registrados</Text>
                ) : (
                  <List
                    size="small"
                    dataSource={movimientosFicha}
                    renderItem={m => (
                      <List.Item
                        style={{ padding: '6px 0' }}
                        actions={soloLectura ? undefined : [
                          <Button
                            key="edit"
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => { setMostrarFicha(false); abrirEdicionMovimiento(p, m) }}
                          />,
                          <Popconfirm
                            key="del"
                            title="¿Eliminar movimiento?"
                            description="Se ajustará el stock del producto"
                            onConfirm={() => eliminarMovimiento(p, m)}
                            okText="Sí"
                            cancelText="No"
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>,
                        ]}
                      >
                        <div style={{ width: '100%' }}>
                          <Space>
                            {m.tipo === 'entrada'
                              ? <ArrowUpOutlined style={{ color: '#52c41a' }} />
                              : <ArrowDownOutlined style={{ color: '#ff4d4f' }} />}
                            <Text strong style={{ fontSize: 12 }}>
                              {m.tipo === 'entrada' ? '+' : '-'}{m.cantidad} {p.unidad_medida}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {dayjs(m.fecha || m.created_at).format('DD/MM/YYYY')}
                            </Text>
                          </Space>
                          {(m.motivo || m.parcela_nombre) && (
                            <div><Text type="secondary" style={{ fontSize: 11 }}>
                              {m.motivo}{m.motivo && m.parcela_nombre ? ' · ' : ''}{m.parcela_nombre ? `Parcela: ${m.parcela_nombre}` : ''}
                            </Text></div>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </>
          )
        })()}
      </Drawer>

      {/* Drawer gestionar categorías */}
      <Drawer
        title="Gestionar categorías"
        open={mostrarCategorias}
        onClose={() => { setMostrarCategorias(false); setCatEditando(null); formCat.resetFields() }}
        width={isMobile ? '100%' : 420}
      >
        <Form
          form={formCat}
          layout="vertical"
          onFinish={guardarCategoria}
          initialValues={{ color: 'default', orden: 0 }}
        >
          <Row gutter={8}>
            <Col span={10}>
              <Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: 'Obligatorio' }]}>
                <Input placeholder="Ej: foliar" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item label="Color" name="color">
                <Select>
                  {COLORES_DISPONIBLES.map(c => (
                    <Select.Option key={c.value} value={c.value}>
                      <Tag color={c.value} style={{ marginRight: 6 }}>●</Tag>{c.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="Orden" name="orden">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" htmlType="submit" icon={catEditando ? <EditOutlined /> : <PlusOutlined />}>
              {catEditando ? 'Actualizar' : 'Añadir'}
            </Button>
            {catEditando && (
              <Button onClick={() => { setCatEditando(null); formCat.resetFields(); formCat.setFieldsValue({ color: 'default', orden: 0 }) }}>
                Cancelar edición
              </Button>
            )}
          </Space>
        </Form>

        <List
          size="small"
          bordered
          dataSource={categorias}
          renderItem={cat => {
            const enUso = productos.filter(p => p.categoria === cat.nombre).length
            return (
              <List.Item
                actions={[
                  <Button
                    key="edit"
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setCatEditando(cat)
                      formCat.setFieldsValue({ nombre: cat.nombre, color: cat.color, orden: cat.orden })
                    }}
                  />,
                  <Popconfirm
                    key="del"
                    title={enUso > 0 ? `${enUso} producto(s) en uso` : '¿Eliminar?'}
                    description={enUso > 0 ? 'Se te pedirá reasignar los productos' : null}
                    onConfirm={() => eliminarCategoria(cat)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <Space>
                  <Tag color={cat.color}>{cat.nombre}</Tag>
                  {enUso > 0 && <Text type="secondary" style={{ fontSize: 11 }}>({enUso})</Text>}
                </Space>
              </List.Item>
            )
          }}
          locale={{ emptyText: 'Sin categorías' }}
        />
      </Drawer>

      {/* Drawer movimiento */}
      <Drawer
        title={`${movEditando ? 'Editar movimiento' : 'Movimiento'}: ${productoMov?.nombre || ''}`}
        open={mostrarMovimiento}
        onClose={() => { setMostrarMovimiento(false); setMovEditando(null) }}
        width={isMobile ? '100%' : 360}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setMostrarMovimiento(false); setMovEditando(null) }}>Cancelar</Button>
            <Button type="primary" onClick={() => formMov.submit()}>{movEditando ? 'Guardar' : 'Registrar'}</Button>
          </Space>
        }
      >
        <Form form={formMov} layout="vertical" onFinish={guardarMovimiento}>
          <Form.Item label="Tipo" name="tipo" rules={[{ required: true }]}>
            <Select onChange={(v) => setTipoMov(v)}>
              <Select.Option value="entrada"><ArrowUpOutlined style={{ color: 'green' }} /> Entrada (compra)</Select.Option>
              <Select.Option value="salida"><ArrowDownOutlined style={{ color: 'red' }} /> Salida (uso)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true, message: 'Obligatorio' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Selecciona fecha" />
          </Form.Item>
          <Form.Item label={`Cantidad (${productoMov?.unidad_medida || ''})`} name="cantidad" rules={[{ required: true, message: 'Obligatorio' }]}>
            <InputNumber min={0.1} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          {tipoMov === 'salida' && (
            <>
              <Form.Item label="Cultivo" name="cultivo_id">
                <Select placeholder="¿En qué cultivo se aplica?" allowClear>
                  {cultivos.map(c => <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="Parcelas" name="parcela_ids">
                <Select mode="multiple" placeholder="¿En qué parcelas?" allowClear>
                  {parcelas.map(p => <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>)}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item label="Motivo" name="motivo">
            <Input placeholder="Ej: Compra proveedor, Uso parcela norte..." />
          </Form.Item>
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {/* Filtro periodo */}
        <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Text strong style={{ fontSize: '12px', color: '#1B3A6B' }}>Periodo gasto:</Text>
          {[
            { key: 'mes', label: 'Este mes' },
            { key: 'anio', label: 'Este año' },
            { key: 'todo', label: 'Todo' },
            { key: 'custom', label: 'Personalizado' },
          ].map(p => (
            <Tag
              key={p.key}
              color={filtroPeriodo === p.key ? '#1B3A6B' : undefined}
              onClick={() => { setFiltroPeriodo(p.key); if (p.key !== 'custom') setFechasCustom(null) }}
              style={{ cursor: 'pointer', fontSize: '11px' }}
            >
              {p.label}
            </Tag>
          ))}
          {filtroPeriodo === 'custom' && (
            <DatePicker.RangePicker
              size="small"
              format="DD/MM/YYYY"
              value={fechasCustom}
              onChange={(dates) => setFechasCustom(dates)}
              style={{ fontSize: '11px' }}
            />
          )}
        </div>

        {/* Stats */}
        <Row gutter={8} style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Productos</Text>} value={stats.total_productos} valueStyle={{ fontSize: '18px', color: '#1B3A6B' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Stock bajo</Text>} value={stats.stock_bajo} valueStyle={{ fontSize: '18px', color: stats.stock_bajo > 0 ? '#ff4d4f' : '#52c41a' }} prefix={stats.stock_bajo > 0 ? <WarningOutlined /> : null} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Valor stock</Text>} value={stats.valor_total} precision={0} suffix="€" valueStyle={{ fontSize: '18px', color: '#E65100' }} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ borderRadius: '10px', textAlign: 'center' }}>
              <Statistic title={<Text style={{ fontSize: '11px' }}>Gastado</Text>} value={stats.gastado} precision={0} suffix="€" valueStyle={{ fontSize: '18px', color: '#c62828' }} prefix={<EuroOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* Buscador */}
        <div style={{ marginBottom: '12px' }}>
          <Input
            allowClear
            placeholder="Buscar producto por nombre..."
            prefix={<SearchOutlined style={{ color: '#1B3A6B' }} />}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>

        {/* Filtro categorías */}
        <div style={{ marginBottom: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <Tag
            color={!filtroCategoria ? '#1B3A6B' : undefined}
            onClick={() => setFiltroCategoria(null)}
            style={{ cursor: 'pointer', fontSize: '12px' }}
          >
            Todos ({productos.length})
          </Tag>
          {categoriasUsadas.map(cat => (
            <Tag
              key={cat}
              color={filtroCategoria === cat ? colorCategoria(cat) || 'blue' : undefined}
              onClick={() => setFiltroCategoria(filtroCategoria === cat ? null : cat)}
              style={{ cursor: 'pointer', fontSize: '12px' }}
            >
              {cat} ({productos.filter(p => p.categoria === cat).length})
            </Tag>
          ))}
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : productosFiltrados.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay productos{filtroCategoria ? ` en "${filtroCategoria}"` : ''}{textoBusqueda ? ` que coincidan con "${busqueda}"` : ''}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>{textoBusqueda ? 'Prueba con otro término' : 'Pulsa "Nuevo" para añadir uno'}</Text>
              </Space>
            }
            style={{ marginTop: '40px' }}
          />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {productosConStock.map(p => {
              const stockBajo = p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo
              return (
                <Card key={p.id} size="small" hoverable onClick={() => abrirFicha(p)} style={{ borderRadius: '12px', borderLeft: stockBajo ? '3px solid #ff4d4f' : '3px solid #d9d9d9', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Text strong style={{ fontSize: '14px', color: '#1B3A6B' }}>{p.nombre}</Text>
                        <Tag color={colorCategoria(p.categoria)} style={{ fontSize: '10px' }}>{p.categoria}</Tag>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <Text style={{ fontSize: '18px', fontWeight: 'bold', color: stockBajo ? '#ff4d4f' : '#2E7D32' }}>
                          {p.stock_actual} {p.unidad_medida}
                        </Text>
                        {stockBajo && <Text type="danger" style={{ fontSize: '11px', marginLeft: '8px' }}><WarningOutlined /> Stock bajo</Text>}
                      </div>
                      {p.precio_unitario > 0 && (() => {
                        const iva = p.iva ?? 10
                        const precioConIva = (p.precio_unitario * (1 + iva / 100))
                        const valorTotal = (p.stock_actual * precioConIva)
                        return (
                          <>
                            <div>
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                Base: {p.precio_unitario} €/{p.unidad_medida} + {iva}% IVA → <Text strong style={{ fontSize: '11px', color: '#E65100' }}>{precioConIva.toFixed(2)} €/{p.unidad_medida}</Text>
                              </Text>
                            </div>
                            <div>
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                Valor stock: {valorTotal.toFixed(2)} €
                              </Text>
                            </div>
                          </>
                        )
                      })()}
                      {p.fecha_compra && (
                        <div><Text type="secondary" style={{ fontSize: '11px' }}><CalendarOutlined /> {dayjs(p.fecha_compra).format('DD/MM/YYYY')}</Text></div>
                      )}
                    </div>
                    {!soloLectura && (
                      <Space direction="vertical" size={4} onClick={(e) => e.stopPropagation()}>
                        <Button size="small" type="primary" onClick={() => abrirMovimiento(p)} style={{ fontSize: '11px', background: '#1B3A6B' }}>
                          <InboxOutlined /> Movimiento
                        </Button>
                        <Space size={4}>
                          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(p)} />
                          <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(p.id)} okText="Sí" cancelText="No">
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      </Space>
                    )}
                  </div>
                </Card>
              )
            })}

            {productosSinStock.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0 4px' }}>
                <div style={{ flex: 1, height: '1px', background: '#d9d9d9' }} />
                <Text strong style={{ fontSize: '12px', color: '#999', letterSpacing: '1px' }}>
                  SIN STOCK ({productosSinStock.length})
                </Text>
                <div style={{ flex: 1, height: '1px', background: '#d9d9d9' }} />
              </div>
            )}

            {productosSinStock.map(p => (
              <Card key={p.id} size="small" hoverable onClick={() => abrirFicha(p)} style={{ borderRadius: '12px', borderLeft: '3px solid #bfbfbf', opacity: 0.65, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Text strong style={{ fontSize: '14px', color: '#595959' }}>{p.nombre}</Text>
                      <Tag color={colorCategoria(p.categoria)} style={{ fontSize: '10px' }}>{p.categoria}</Tag>
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#999' }}>
                        0 {p.unidad_medida}
                      </Text>
                    </div>
                    {p.fecha_compra && (
                      <div><Text type="secondary" style={{ fontSize: '11px' }}><CalendarOutlined /> {dayjs(p.fecha_compra).format('DD/MM/YYYY')}</Text></div>
                    )}
                  </div>
                  {!soloLectura && (
                    <Space direction="vertical" size={4} onClick={(e) => e.stopPropagation()}>
                      <Button size="small" type="primary" onClick={() => abrirMovimiento(p)} style={{ fontSize: '11px', background: '#1B3A6B' }}>
                        <InboxOutlined /> Movimiento
                      </Button>
                      <Space size={4}>
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(p)} />
                        <Popconfirm title="¿Eliminar?" onConfirm={() => eliminar(p.id)} okText="Sí" cancelText="No">
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    </Space>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>
    </div>
  )
}
